/**
 * Optimistic free-shipping progress bar.
 *
 * The cart drawer re-renders server-side on every mutation and morphs the bar
 * back in with authoritative values — but that waits on a network round-trip,
 * so the fill visibly lags the quantity stepper click. This listens for the
 * theme's `quantity-selector:update` event (dispatched synchronously on click)
 * and updates the bar immediately from data rendered into the DOM by
 * `snippets/free-shipping-bar.liquid`. When the server morph lands ~300ms later
 * it overwrites the bar with the identical value, so there is no visible jump.
 *
 * A single document-level listener is attached once. The bar markup (including
 * the data island) is replaced on each morph, so all state is read from the DOM
 * at event time rather than cached — and written back optimistically so rapid
 * consecutive clicks compound correctly before the morph reconciles.
 */
(() => {
  if (window.__freeShippingBarInit) return;
  window.__freeShippingBarInit = true;

  /**
   * @param {number} cents
   * @returns {string} e.g. "$45.00"
   */
  const formatMoney = (cents) =>
    '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  /**
   * @param {HTMLElement} bar
   * @param {number} threshold
   * @param {number} subtotal
   */
  const render = (bar, threshold, subtotal) => {
    const remaining = threshold - subtotal;
    const progress = Math.min(100, Math.round((subtotal * 100) / threshold));
    const eligible = remaining <= 0;

    const fill = bar.querySelector('.free-shipping-bar__fill');
    const track = bar.querySelector('.free-shipping-bar__track');
    const message = bar.querySelector('[data-free-shipping-message]');

    if (fill instanceof HTMLElement) fill.style.width = progress + '%';
    if (track) track.setAttribute('aria-valuenow', String(progress));
    bar.classList.toggle('free-shipping-bar--eligible', eligible);

    if (message) {
      if (eligible) {
        message.textContent = bar.dataset.eligibleText || 'You are eligible for free shipping!';
      } else {
        // Same editable template the server renders (theme setting); [amount]
        // is swapped for the live remaining amount so wording stays in sync.
        const template = bar.dataset.progressTemplate || 'Spend [amount] more and get Free Shipping!';
        message.textContent = template.replace('[amount]', formatMoney(remaining));
      }
    }

    // Persist so the next click compounds from this value until the morph resets it.
    bar.dataset.subtotal = String(subtotal);
  };

  document.addEventListener('quantity-selector:update', (event) => {
    const detail = /** @type {{ quantity?: number, cartLine?: number }} */ (
      /** @type {CustomEvent} */ (event).detail
    );
    if (!detail || detail.cartLine == null || detail.quantity == null) return;

    const bar = /** @type {HTMLElement | null} */ (document.querySelector('[data-free-shipping-bar]'));
    const linesEl = document.querySelector('[data-free-shipping-lines]');
    if (!bar || !linesEl) return;

    const threshold = Number(bar.dataset.threshold);
    const subtotal = Number(bar.dataset.subtotal);
    if (!threshold || Number.isNaN(subtotal)) return;

    let lines;
    try {
      lines = JSON.parse(linesEl.textContent || '[]');
    } catch {
      return;
    }

    const line = lines.find((l) => l.line === detail.cartLine);
    if (!line) return;

    const newSubtotal = subtotal + line.price * (detail.quantity - line.qty);

    // Keep the island in sync so rapid repeat clicks on the same line compound.
    line.qty = detail.quantity;
    linesEl.textContent = JSON.stringify(lines);

    render(bar, threshold, Math.max(0, newSubtotal));
  });
})();
